/**
 * Who is using the allowance — by person, or rolled up by group.
 *
 * The old page showed two side-by-side lists — this month and all time — of
 * the same ten people, which doubled the reading for one comparison nobody was
 * making. One sortable table with a period switch says the same thing in half
 * the space, and adds the column that makes it useful for chargeback: each
 * row's share of the total.
 *
 * "By group" rolls up every current member's activity per group — it is not
 * a re-slice of the top-10 individual leaderboard above it (which would
 * silently under-count any group whose members aren't individually
 * top-ranked); the backend aggregates it independently across every member.
 *
 * The period is the page-wide Analysis Period control, not a tab-local
 * choice — `activity`/`groupActivity` already arrive scoped to it (the
 * `period` bucket the backend computes alongside `current_month`/`all_time`).
 *
 * The rows are only the top of the table (ten people, ten groups), so the Total
 * row and every share are measured against `totals`: everyone in the period
 * (summary.user_activity_totals.period). Summing the listed rows would present
 * the top ten as the whole organisation and overstate each one's share. A
 * person can belong to several groups, so group figures are not additive
 * either: group shares are taken of the organisation's requests, never of the
 * sum of the group rows. Without `totals` (an older backend) the rows' own sum
 * is all there is.
 *
 * In the person view an "Everyone else" row carries the difference between the
 * total and the listed rows, so the rows add up to the total and the shares
 * read against it. Groups get no such row: they overlap, so they never add up.
 *
 * In the person view each row, and "Everyone else", opens a drawer with the
 * templates behind its requests (`onSelectPerson` / `onSelectOthers`). Groups
 * have no drill-down: they overlap, so their templates would not add up either.
 */
import { useMemo, useState } from 'react'
import { AppWindow, ArrowDown, ArrowUp, ChevronRight, Layers, Users } from 'lucide-react'
import UserAvatar from '../UserAvatar'
import { EmptyState, Segmented, ShareBar } from './BillingPrimitives'
import { formatInt } from '../../utils/billing'
import { percentParts } from '../../utils/apportion'
import { isServiceAccount } from '../../utils/requestBreakdown'
import { isAllTimeLabel } from './useBillingData'

const VIEWS = [
  { value: 'person', label: 'By person' },
  { value: 'group', label: 'By group' },
]

const COLUMNS = [
  { key: 'requests', label: 'Requests' },
  { key: 'documents', label: 'Documents' },
]
// The pages each person was charged for in the period (the billing ledger:
// completed processing only). Shown per person; null means not available.
const BILLABLE = { key: 'billable_pages', label: 'Billable pages' }

export default function PeoplePanel({
  activity, groupActivity, totals: orgTotals = null, periodLabel, onSelectPerson = null, onSelectOthers = null,
}) {
  const [view, setView] = useState('person')
  const [sort, setSort] = useState({ key: 'requests', dir: 'desc' })

  const source = view === 'group' ? groupActivity : activity

  const rows = useMemo(() => {
    const list = [...(source?.period || [])]
    return list.sort((a, b) => {
      const diff = (b[sort.key] || 0) - (a[sort.key] || 0)
      return sort.dir === 'desc' ? diff : -diff
    })
  }, [source, sort])

  const isGroup = view === 'group'
  const showBillable = !isGroup && rows.some(r => r.billable_pages !== undefined)
  const billableKnown = rows.every(r => r.billable_pages !== null)
  const columns = showBillable ? [...COLUMNS, BILLABLE] : COLUMNS

  const { totals, rest, shares } = useMemo(() => {
    const own = {
      requests: rows.reduce((s, r) => s + (r.requests || 0), 0),
      documents: rows.reduce((s, r) => s + (r.documents || 0), 0),
      billable_pages: rows.reduce((s, r) => s + (r.billable_pages || 0), 0),
    }
    const known = key => Number.isFinite(Number(orgTotals?.[key])) && orgTotals?.[key] !== null
    const all = {
      requests: known('requests') ? Number(orgTotals.requests) : own.requests,
      documents: known('documents') ? Number(orgTotals.documents) : own.documents,
      billable_pages: known('billable_pages') ? Number(orgTotals.billable_pages) : own.billable_pages,
    }
    // Everyone below the listed rows: people only, and only when the totals
    // cover more than the rows do.
    let others = null
    if (!isGroup && orgTotals) {
      const diff = key => (known(key) ? Math.max(0, all[key] - own[key]) : 0)
      const candidate = {
        requests: diff('requests'),
        documents: diff('documents'),
        billable_pages: showBillable && billableKnown ? diff('billable_pages') : null,
      }
      if (candidate.requests > 0 || candidate.documents > 0 || candidate.billable_pages > 0) others = candidate
    }
    // Shares of the real total. When the rows (with "Everyone else") make up
    // exactly that total, whole-number shares that add up to 100.
    const counts = [...rows.map(r => r.requests || 0), ...(others ? [others.requests] : [])]
    const parts = counts.reduce((s, n) => s + n, 0) === all.requests && all.requests > 0
      ? percentParts(counts)
      : counts.map(n => (all.requests ? Math.round((n / all.requests) * 100) : 0))
    return { totals: all, rest: others, shares: parts }
  }, [rows, orgTotals, isGroup, showBillable, billableKnown])

  const toggleSort = key =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }))

  return (
    <div className="p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Usage by {isGroup ? 'group' : 'person'}
          </h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {isGroup
              ? 'Every current member’s activity, rolled up per group.'
              : `Top by volume. Attribution follows whoever raised the request.${onSelectPerson ? ' Select a row to see the templates behind it.' : ''}`}
            {periodLabel ? ` ${isAllTimeLabel(periodLabel) ? 'All time.' : `In the last ${periodLabel}.`}` : ''}
          </p>
        </div>
        <Segmented options={VIEWS} value={view} onChange={setView} ariaLabel="Usage by person or group" />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={isGroup ? Layers : Users}
          title={`No ${isGroup ? 'group' : ''} activity in this period`}
          body={
            isGroup
              ? 'A group needs at least one member with activity to appear here. Groups are managed under Users & Groups.'
              : 'Requests raised by your users will be attributed here.'
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800">
                <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 py-2 pr-3">
                  {isGroup ? 'Group' : 'Person'}
                </th>
                {columns.map(col => (
                  <th key={col.key} className="py-2 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider
                                 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300
                                 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
                      aria-sort={sort.key === col.key ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    >
                      {col.label}
                      {sort.key === col.key
                        ? (sort.dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
                        : <span className="w-3" />}
                    </button>
                  </th>
                ))}
                <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 py-2 pl-3 w-[180px]">
                  Share of requests
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const select = !isGroup && onSelectPerson ? () => onSelectPerson({ ...row, sharePct: shares[i] }) : null
                const app = !isGroup && isServiceAccount(row.email)
                return (
                <tr
                  key={row.id ?? row.email ?? i}
                  {...(select ? {
                    onClick: select,
                    onKeyDown: e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select() } },
                    tabIndex: 0,
                    role: 'button',
                    'aria-label': `View templates used by ${row.username}`,
                  } : {})}
                  className={`border-b border-gray-100 dark:border-gray-800/60 last:border-0
                             hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors animate-line-in
                             ${select ? 'group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-500' : ''}`}
                  style={{ animationDelay: `${i * 28}ms` }}
                >
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-[11px] font-bold text-gray-300 dark:text-gray-600 w-4 text-center tabular-nums">
                        {i + 1}
                      </span>
                      {isGroup ? (
                        <span className="w-7 h-7 flex-shrink-0 rounded-full bg-gray-100 dark:bg-gray-800
                                         flex items-center justify-center text-gray-500 dark:text-gray-400">
                          <Layers className="w-3.5 h-3.5" />
                        </span>
                      ) : app ? (
                        <span className="w-8 h-8 flex-shrink-0 rounded-full bg-navy-50 dark:bg-navy-900/40
                                         flex items-center justify-center text-navy-600 dark:text-navy-300">
                          <AppWindow className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <UserAvatar user={{ id: row.id, username: row.username, email: row.email }} size="sm" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-[13px]">
                          {isGroup ? row.name : row.username}
                        </p>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                          {isGroup
                            ? `${formatInt(row.member_count)} ${row.member_count === 1 ? 'member' : 'members'}`
                            : app ? 'App sign-in (Outlook add-in or API)' : row.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                    {formatInt(row.requests)}
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {formatInt(row.documents || 0)}
                  </td>
                  {showBillable && (
                    <td className="py-2.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                      {row.billable_pages === null ? '–' : formatInt(row.billable_pages || 0)}
                    </td>
                  )}
                  <td className="py-2.5 pl-3">
                    <div className="flex items-center gap-1">
                      <div className="flex-1">
                        <ShareBar
                          value={row.requests || 0}
                          max={totals.requests}
                          label={`${shares[i]}%`}
                        />
                      </div>
                      {select && (
                        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600
                                                 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors" />
                      )}
                    </div>
                  </td>
                </tr>
                )
              })}
              {rest && (
                <tr
                  {...(onSelectOthers ? {
                    onClick: () => onSelectOthers({ listedIds: rows.map(r => r.id), others: rest }),
                    onKeyDown: e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelectOthers({ listedIds: rows.map(r => r.id), others: rest })
                      }
                    },
                    tabIndex: 0,
                    role: 'button',
                    'aria-label': 'View everyone not listed above',
                  } : {})}
                  className={`border-b border-gray-100 dark:border-gray-800/60 last:border-0
                              ${onSelectOthers ? `group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-500` : ''}`}
                >
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-4" />
                      <span className="w-7 h-7 flex-shrink-0 rounded-full bg-gray-100 dark:bg-gray-800
                                       flex items-center justify-center text-gray-500 dark:text-gray-400">
                        <Users className="w-3.5 h-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-700 dark:text-gray-300 truncate text-[13px]">Everyone else</p>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">Everyone not listed above</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-700 dark:text-gray-300">
                    {formatInt(rest.requests)}
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {formatInt(rest.documents)}
                  </td>
                  {showBillable && (
                    <td className="py-2.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                      {rest.billable_pages === null ? '–' : formatInt(rest.billable_pages)}
                    </td>
                  )}
                  <td className="py-2.5 pl-3">
                    <div className="flex items-center gap-1">
                      <div className="flex-1">
                        <ShareBar value={rest.requests} max={totals.requests} label={`${shares[rows.length]}%`} />
                      </div>
                      {onSelectOthers && (
                        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600
                                                 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors" />
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-800">
                <td className="py-2.5 pr-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Total
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {formatInt(totals.requests)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {formatInt(totals.documents)}
                </td>
                {showBillable && (
                  <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                    {billableKnown ? formatInt(totals.billable_pages) : '–'}
                  </td>
                )}
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
